import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, FormTextArea, FormInput, FormSelect, FormDate, FormActions } from "../Controls/Form";
import UserData from "../Controls/UserData";
import CustomButton from "../Controls/CustomButton";
import ListIconButton from "../Controls/ListIconButton";
import EmployerSelect from "../Modals/EmployerSelect";
import EmployerEditModal from "../Modals/EmployerEditModal";
import EmploymentConnectionEditModal from "../Modals/EmploymentConnectionEditModal";
import "../../style/scss/modal.scss";
import "../../style/scss/FormActions.scss";
import API from "@src/API.js";
import { toast } from "react-toastify";
import { FaTrashAlt, FaExchangeAlt } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import { refreshEmploymentData, useEmploymentQuery } from "../../query/useEmployment";
import { refreshPersonSkillsData } from "../../query/usePersonSkills";

function resolveEmployerById(employers, employerId, nameHint = "") {
  const id = Number(employerId) || 0;
  if (!id) return null;
  const fromList = (employers || []).find((e) => Number(e.ID ?? e.id) === id);
  if (fromList) return fromList;
  return { ID: id, Name: nameHint || `Employer #${id}` };
}

function emptyEmployment() {
  return {
    ID: 0,
    EmployerID: null,
    IntermediaryID: null,
    ClientID: null,
    Title: "",
    ShortDescription: "",
    Responsibilities: "",
    StartDate: null,
    EndDate: null,
    Email: "",
    EmploymentTypeID: 10,
    PayRate: 0,
    Skills: [],
    Careers: [],
    Searchable: true,
  };
}

function TabRequiredDot() {
  return <span className="tab-required-dot" aria-hidden title="Required fields missing" />;
}

const ORG_SLOTS = [
  {
    key: "employer",
    label: "Employer",
    hint: "Organization that pays payroll taxes for you.",
    required: true,
  },
  {
    key: "client",
    label: "Client",
    hint: "Business paying for the work (optional).",
    required: false,
  },
  {
    key: "intermediary",
    label: "Intermediary",
    hint: "Company in between — recruiter, staffing, H1B sponsor, etc. (optional).",
    required: false,
  },
];

function EmploymentOrgSlot({
  slotKey,
  label,
  hint,
  value,
  invalid,
  onClear,
  onEditCompany,
  onPick,
}) {
  const id = Number(value?.ID ?? value?.id ?? 0) || 0;
  const name = value?.Name ?? value?.name ?? value?.ShortName ?? "";

  return (
    <div
      className={`field employment-org-slot${invalid ? " field-invalid" : ""}`}
      data-testid={`employment-org-${slotKey}`}
    >
      <label className="form-label">{label}</label>
      <p className="fieldHint">{hint}</p>
      {id > 0 ? (
        <div className={`employment-org-row${invalid ? " field-invalid-control" : ""}`}>
          <button
            type="button"
            className="employment-org-name"
            data-testid={`employment-org-edit-${slotKey}`}
            title={`Edit ${name}`}
            onClick={() => onEditCompany?.(id)}
          >
            {name || `Company #${id}`}
          </button>
          <div className="employment-org-actions">
            <ListIconButton
              variant="muted"
              aria-label={`Change ${label}`}
              title="Change organization"
              data-testid={`employment-org-change-${slotKey}`}
              onClick={() => onPick?.(slotKey)}
            >
              <FaExchangeAlt aria-hidden />
            </ListIconButton>
            <ListIconButton
              aria-label={`Remove ${label}`}
              title="Remove from this employment"
              data-testid={`employment-org-remove-${slotKey}`}
              onClick={() => onClear?.(slotKey)}
            >
              <FaTrashAlt aria-hidden />
            </ListIconButton>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`form-input employment-org-empty${invalid ? " field-invalid-control" : ""}`}
          data-testid={`employment-org-add-${slotKey}`}
          onClick={() => onPick?.(slotKey)}
        >
          <MdAdd aria-hidden /> Select {label.toLowerCase()}…
        </button>
      )}
    </div>
  );
}

/**
 * Employment form body. Prefer EmploymentEditModal for UI entry points.
 */
export default function EmploymentRoleEdit({ employmentId: employmentIdProp = 0, onClose }) {
  const id = Number.parseInt(employmentIdProp, 10) || 0;
  const queryClient = useQueryClient();
  const { data: employmentData } = useEmploymentQuery();
  const employers = employmentData?.employers || [];
  const [lookups] = useState(UserData.Lookups() || {});
  const [personEmployment, updatePersonEmployment] = useState(() => emptyEmployment());
  const [busy, setBusy] = useState(false);
  const [employer, setEmployer] = useState(null);
  const [client, setClient] = useState(null);
  const [intermediary, setIntermediary] = useState(null);
  const [connections, setConnections] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [tab, setTab] = useState("description");
  const [fieldErrors, setFieldErrors] = useState({});
  const [pickSlot, setPickSlot] = useState(null); // employer | client | intermediary
  const [editCompanyId, setEditCompanyId] = useState(null);

  useEffect(() => {
    if (!id) {
      updatePersonEmployment(emptyEmployment());
      setEmployer(null);
      setClient(null);
      setIntermediary(null);
      return;
    }
    const list = employmentData?.employmentHistory || [];
    const found = list.find((e) => Number(e.ID || e.id) === id);
    if (!found) return;
    updatePersonEmployment(found);
    setEmployer(resolveEmployerById(employers, found.EmployerID, found.EmployerName));
    setClient(resolveEmployerById(employers, found.ClientID, found.ClientName));
    setIntermediary(
      resolveEmployerById(employers, found.IntermediaryID, found.IntermediaryName)
    );
  }, [id, employmentData, employers]);

  function hydrateOrg(orgId, setter, current) {
    const employerId = Number(orgId) || 0;
    if (!employerId) return;
    if (current?.Name && !String(current.Name).startsWith("Employer #")) return;
    let cancelled = false;
    (async () => {
      const resp = await API.getAsync(`/Employer/${employerId}`);
      if (cancelled || !resp?.ok || !resp.jsonData) return;
      setter(resp.jsonData);
    })();
    return () => {
      cancelled = true;
    };
  }

  useEffect(
    () => hydrateOrg(personEmployment?.EmployerID, setEmployer, employer),
    [personEmployment?.EmployerID]
  );
  useEffect(
    () => hydrateOrg(personEmployment?.ClientID, setClient, client),
    [personEmployment?.ClientID]
  );
  useEffect(
    () => hydrateOrg(personEmployment?.IntermediaryID, setIntermediary, intermediary),
    [personEmployment?.IntermediaryID]
  );

  // Clear field errors as values become valid
  useEffect(() => {
    setFieldErrors((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      if (String(personEmployment.Title || "").trim()) delete next.Title;
      if (String(personEmployment.ShortDescription || "").trim()) delete next.ShortDescription;
      if (String(personEmployment.Responsibilities || "").trim()) delete next.Responsibilities;
      if (Number(employer?.ID ?? personEmployment.EmployerID) > 0) delete next.EmployerID;
      const payRate = Number(personEmployment.PayRate) || 0;
      const employmentTypeId = Number(personEmployment.EmploymentTypeID) || 10;
      if (employmentTypeId === 60 || payRate > 0) delete next.PayRate;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [personEmployment, employer]);

  const employmentId = Number(personEmployment.ID || personEmployment.id || id) || 0;

  async function loadConnections(empId) {
    if (!empId) {
      setConnections([]);
      return;
    }
    const resp = await API.getAsync(`/PersonEmployment/${empId}/Connections`);
    if (resp?.ok) setConnections(resp.jsonData || []);
  }

  useEffect(() => {
    if (employmentId > 0) loadConnections(employmentId);
  }, [employmentId]);

  function close() {
    onClose?.();
  }

  function onOrgSelect(role, selected) {
    const selectedId = selected ? Number(selected?.ID ?? selected?.id ?? 0) || null : null;
    const name = selected?.Name ?? selected?.name ?? "";
    const patch =
      role === "employer"
        ? { EmployerID: selectedId, EmployerName: name }
        : role === "client"
          ? { ClientID: selectedId, ClientName: name }
          : { IntermediaryID: selectedId, IntermediaryName: name };
    if (role === "employer") setEmployer(selected ? { ID: selectedId, Name: name, ...selected } : null);
    if (role === "client") setClient(selected ? { ID: selectedId, Name: name, ...selected } : null);
    if (role === "intermediary")
      setIntermediary(selected ? { ID: selectedId, Name: name, ...selected } : null);
    updatePersonEmployment((prev) => ({ ...prev, ...patch }));
    setPickSlot(null);
  }

  function orgValueForSlot(slotKey) {
    if (slotKey === "employer") return employer;
    if (slotKey === "client") return client;
    return intermediary;
  }

  async function refreshEditedCompany(companyId) {
    if (!companyId) return;
    const resp = await API.getAsync(`/Employer/${companyId}`);
    if (!resp?.ok || !resp.jsonData) return;
    const data = resp.jsonData;
    const eid = Number(data.ID ?? data.id) || 0;
    if (Number(employer?.ID) === eid) {
      setEmployer(data);
      updatePersonEmployment((prev) => ({
        ...prev,
        EmployerName: data.Name ?? data.name,
      }));
    }
    if (Number(client?.ID) === eid) {
      setClient(data);
      updatePersonEmployment((prev) => ({
        ...prev,
        ClientName: data.Name ?? data.name,
      }));
    }
    if (Number(intermediary?.ID) === eid) {
      setIntermediary(data);
      updatePersonEmployment((prev) => ({
        ...prev,
        IntermediaryName: data.Name ?? data.name,
      }));
    }
  }

  function computeErrors() {
    const errors = {};
    if (!String(personEmployment.Title || "").trim()) errors.Title = true;
    if (!String(personEmployment.ShortDescription || "").trim()) errors.ShortDescription = true;
    if (!String(personEmployment.Responsibilities || "").trim()) errors.Responsibilities = true;
    const employerId = Number(employer?.ID ?? personEmployment.EmployerID) || null;
    if (!employerId) errors.EmployerID = true;
    const payRate = Number(personEmployment.PayRate) || 0;
    const employmentTypeId = Number(personEmployment.EmploymentTypeID) || 10;
    if (employmentTypeId !== 60 && payRate <= 0) errors.PayRate = true;
    return errors;
  }

  function tabHasError(errors, tabName) {
    if (tabName === "description")
      return !!(errors.Title || errors.ShortDescription || errors.Responsibilities);
    if (tabName === "organizations") return !!errors.EmployerID;
    if (tabName === "details") return !!errors.PayRate;
    return false;
  }

  async function onSaveAsync() {
    if (busy) return;
    const errors = computeErrors();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const employerId = Number(employer?.ID ?? personEmployment.EmployerID) || null;
    const payRate = Number(personEmployment.PayRate) || 0;
    const employmentTypeId = Number(personEmployment.EmploymentTypeID) || 10;

    setBusy(true);
    try {
      const payload = {
        ...personEmployment,
        ID: personEmployment.ID || 0,
        EmployerID: employerId,
        ClientID: Number(client?.ID ?? personEmployment.ClientID) || null,
        IntermediaryID: Number(intermediary?.ID ?? personEmployment.IntermediaryID) || null,
        PayRate: payRate,
        EmploymentTypeID: employmentTypeId,
      };
      const ret = await UserData.UpdateAsync("employmentHistory", payload, true);
      if (ret?.ok || ret?.result === "Success") {
        toast.success("Employment saved");
        await refreshEmploymentData(queryClient);
        await refreshPersonSkillsData(queryClient);
        close();
      } else {
        const apiMsg =
          ret?.saveResp?.jsonData?.response ||
          ret?.saveResp?.jsonData?.message ||
          ret?.message;
        toast.error(apiMsg || "Could not save employment");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteAsync() {
    const empId = personEmployment.ID || personEmployment.id;
    if (!empId) {
      close();
      return;
    }
    const resp = await UserData.DeleteAsync("employmentHistory", empId);
    if (resp?.result) {
      toast.success("Employment deleted");
      await refreshEmploymentData(queryClient);
      await refreshPersonSkillsData(queryClient);
      close();
    } else {
      toast.error(resp?.error || "Delete failed");
    }
  }

  async function onRemoveConnection(attachmentId) {
    if (!employmentId || linkBusy) return;
    setLinkBusy(true);
    try {
      const resp = await API.deleteAsync(
        `/PersonEmployment/${employmentId}/Connections/${attachmentId}`
      );
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Could not remove connection");
        return;
      }
      toast.success("Connection removed");
      await loadConnections(employmentId);
    } finally {
      setLinkBusy(false);
    }
  }

  const employmentTypes = lookups.employmentType || lookups.EmploymentType || [];
  const connectionCount = connections.length;
  const hasRequiredErrors = Object.keys(fieldErrors).length > 0;
  const orgOptions = [
    employer?.ID
      ? { role: 1, label: "Employer", name: employer.Name || employer.ShortName, id: employer.ID }
      : null,
    client?.ID
      ? { role: 2, label: "Client", name: client.Name || client.ShortName, id: client.ID }
      : null,
    intermediary?.ID
      ? {
          role: 3,
          label: "Intermediary",
          name: intermediary.Name || intermediary.ShortName,
          id: intermediary.ID,
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={personEmployment} onUpdate={updatePersonEmployment}>
        <div className="cm-modal-tabs" role="tablist" aria-label="Employment sections">
          <button
            type="button"
            role="tab"
            id="employment-tab-description"
            aria-selected={tab === "description"}
            aria-controls="employment-panel-description"
            className={tab === "description" ? "is-active" : undefined}
            data-testid="employment-tab-description"
            onClick={() => setTab("description")}
          >
            Description
            {tabHasError(fieldErrors, "description") ? <TabRequiredDot /> : null}
          </button>
          <button
            type="button"
            role="tab"
            id="employment-tab-organizations"
            aria-selected={tab === "organizations"}
            aria-controls="employment-panel-organizations"
            className={tab === "organizations" ? "is-active" : undefined}
            data-testid="employment-tab-organizations"
            onClick={() => setTab("organizations")}
          >
            Organizations
            {tabHasError(fieldErrors, "organizations") ? <TabRequiredDot /> : null}
          </button>
          <button
            type="button"
            role="tab"
            id="employment-tab-details"
            aria-selected={tab === "details"}
            aria-controls="employment-panel-details"
            className={tab === "details" ? "is-active" : undefined}
            data-testid="employment-tab-details"
            onClick={() => setTab("details")}
          >
            Details
            {tabHasError(fieldErrors, "details") ? <TabRequiredDot /> : null}
          </button>
          <button
            type="button"
            role="tab"
            id="employment-tab-connections"
            aria-selected={tab === "connections"}
            aria-controls="employment-panel-connections"
            className={tab === "connections" ? "is-active" : undefined}
            data-testid="employment-tab-connections"
            onClick={() => setTab("connections")}
          >
            Connections{connectionCount > 0 ? ` (${connectionCount})` : ""}
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="employment-panel-description"
            role="tabpanel"
            aria-labelledby="employment-tab-description"
            aria-hidden={tab !== "description"}
            className={`cm-modal-tab-panel${tab === "description" ? " is-active" : ""}`}
            {...(tab !== "description" ? { inert: "" } : {})}
          >
            <FormInput label="Title" type="text" field="Title" invalid={!!fieldErrors.Title} />
            <FormTextArea
              label="Short Description"
              field="ShortDescription"
              rowsmin={6}
              invalid={!!fieldErrors.ShortDescription}
            />
            <FormTextArea
              label="Responsibilities"
              field="Responsibilities"
              rowsmin={6}
              invalid={!!fieldErrors.Responsibilities}
            />
          </div>

          <div
            id="employment-panel-organizations"
            role="tabpanel"
            aria-labelledby="employment-tab-organizations"
            aria-hidden={tab !== "organizations"}
            className={`cm-modal-tab-panel${tab === "organizations" ? " is-active" : ""}`}
            data-testid="employment-organizations"
            {...(tab !== "organizations" ? { inert: "" } : {})}
          >
            {ORG_SLOTS.map((slot) => (
              <EmploymentOrgSlot
                key={slot.key}
                slotKey={slot.key}
                label={slot.label}
                hint={slot.hint}
                value={orgValueForSlot(slot.key)}
                invalid={slot.key === "employer" && !!fieldErrors.EmployerID}
                onEditCompany={(companyId) => setEditCompanyId(companyId)}
                onPick={(key) => setPickSlot(key)}
                onClear={(key) => onOrgSelect(key, null)}
              />
            ))}
          </div>

          <div
            id="employment-panel-details"
            role="tabpanel"
            aria-labelledby="employment-tab-details"
            aria-hidden={tab !== "details"}
            className={`cm-modal-tab-panel${tab === "details" ? " is-active" : ""}`}
            {...(tab !== "details" ? { inert: "" } : {})}
          >
            <FormDate label="Start Date" field="StartDate" nullable />
            <FormDate label="End Date" field="EndDate" nullable />
            <FormInput label="Work Email" field="Email" type="email" />
            <FormSelect
              label="Employment Type"
              field="EmploymentTypeID"
              options={employmentTypes}
              labelProp="Value"
              valueProp="Key"
            />
            <FormInput
              label="Pay Rate"
              field="PayRate"
              type="float"
              invalid={!!fieldErrors.PayRate}
            />
          </div>

          <div
            id="employment-panel-connections"
            role="tabpanel"
            aria-labelledby="employment-tab-connections"
            aria-hidden={tab !== "connections"}
            className={`cm-modal-tab-panel${tab === "connections" ? " is-active" : ""}`}
            data-testid="employment-connections"
            {...(tab !== "connections" ? { inert: "" } : {})}
          >
            {employmentId > 0 ? (
              <>
                <div className="employment-connections-header">
                  <p className="fieldHint">
                    People you worked with in this role. Invite someone who is not on CareerMaster
                    yet — they can verify after they join.
                  </p>
                  <CustomButton
                    type="button"
                    variant="contained"
                    color="primary"
                    className="form-actions__delete"
                    aria-label="Add employment connection"
                    title="Add employment connection"
                    data-testid="employment-connection-add"
                    onClick={() => setAddOpen(true)}
                  >
                    <MdAdd aria-hidden />
                  </CustomButton>
                </div>

                {connections.length === 0 ? (
                  <p className="fieldHint">No connections for this job yet.</p>
                ) : (
                  <ul
                    className="employment-supervisor-list"
                    data-testid="employment-connection-list"
                  >
                    {connections.map((c) => {
                      const linkId = c.ID ?? c.id;
                      const name = c.Name ?? c.name ?? "Connection";
                      const rel = c.RelationLabel ?? c.relationLabel ?? "Coworker";
                      const status = c.ConnectionStatus ?? c.connectionStatus ?? "";
                      const orgName = c.EmployerName ?? c.employerName ?? "";
                      const orgRole =
                        c.OrganizationRoleLabel ?? c.organizationRoleLabel ?? "";
                      return (
                        <li key={linkId} className="employment-supervisor-item">
                          <div className="employment-supervisor-item__info">
                            <span className="employment-supervisor-item__name">{name}</span>
                            <span className="employment-supervisor-item__rel">{rel}</span>
                            {orgName ? (
                              <span className="employment-supervisor-item__rel">
                                {orgRole ? `${orgRole}: ` : ""}
                                {orgName}
                              </span>
                            ) : null}
                            {status ? (
                              <span className="employment-supervisor-item__rel">{status}</span>
                            ) : null}
                          </div>
                          <ListIconButton
                            aria-label={`Remove ${name}`}
                            title="Remove"
                            data-testid={`remove-employment-connection-${linkId}`}
                            disabled={linkBusy}
                            onClick={() => onRemoveConnection(linkId)}
                          >
                            <FaTrashAlt aria-hidden />
                          </ListIconButton>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            ) : (
              <p className="fieldHint">
                Save this employment first, then return here to add connections.
              </p>
            )}
          </div>
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          requiredHint={hasRequiredErrors}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            personEmployment.ID > 0
              ? async () => {
                  await onDeleteAsync();
                }
              : undefined
          }
          onSaveAsync={async () => {
            await onSaveAsync();
          }}
        />
      </Form>

      {addOpen ? (
        <EmploymentConnectionEditModal
          employmentId={employmentId}
          organizationOptions={orgOptions}
          onClose={(saved) => {
            setAddOpen(false);
            if (saved) loadConnections(employmentId);
          }}
        />
      ) : null}

      {pickSlot ? (
        <EmployerSelect
          hideTrigger
          open
          label={ORG_SLOTS.find((s) => s.key === pickSlot)?.label || "Organization"}
          value={orgValueForSlot(pickSlot)}
          onOpenChange={(next) => {
            if (!next) setPickSlot(null);
          }}
          onSelect={(selected) => onOrgSelect(pickSlot, selected)}
        />
      ) : null}

      {editCompanyId != null ? (
        <EmployerEditModal
          employerId={editCompanyId}
          onClose={async () => {
            const id = editCompanyId;
            setEditCompanyId(null);
            await refreshEditedCompany(id);
          }}
        />
      ) : null}
    </div>
  );
}
